import { ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiProvider } from 'wagmi'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { wagmiConfig } from './config/wagmi'
import { LanguageProvider } from './i18n'
import { ThemeProvider } from './contexts/ThemeContext'
import { useBridgeConfig } from './hooks/useBridgeConfig'

// Layouts
import { MainLayout } from './components/layout/MainLayout'
import { AdminLayout } from './components/layout/AdminLayout'

// User pages
import { LandingPage } from './pages/user/LandingPage'
import { HomePage } from './pages/user/HomePage'
import { HistoryPage } from './pages/user/HistoryPage'
import { PrivacyPage } from './pages/user/PrivacyPage'
import { TermsPage } from './pages/user/TermsPage'
import { P2PPage } from './pages/user/P2PPage'
import { MyOrdersPage } from './pages/user/MyOrdersPage'
import { MyTradesPage } from './pages/user/MyTradesPage'

// Admin pages
import { DashboardPage } from './pages/admin/DashboardPage'
import { QueuePage } from './pages/admin/QueuePage'
import { ConfigPage } from './pages/admin/ConfigPage'
import P2PContractConfigPage from './pages/admin/P2PContractConfigPage'
import { AuditPage } from './pages/admin/AuditPage'
import { HoldersPage } from './pages/admin/HoldersPage'
import { WalletsPage } from './pages/admin/WalletsPage'
import { TemplatesPage } from './pages/admin/TemplatesPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
        <LanguageProvider>
        <BrowserRouter>
          <Routes>
            {/* User routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="bridge" element={<HomePage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="p2p" element={<P2PGate><P2PPage /></P2PGate>} />
              <Route path="p2p/orders" element={<P2PGate><MyOrdersPage /></P2PGate>} />
              <Route path="p2p/trades" element={<P2PGate><MyTradesPage /></P2PGate>} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="terms" element={<TermsPage />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="queue" element={<QueuePage />} />
              <Route path="wallets" element={<WalletsPage />} />
              <Route path="config" element={<ConfigPage />} />
              <Route path="p2p-contract" element={<P2PContractConfigPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="holders" element={<HoldersPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'bg-dark-800 text-white border border-dark-700',
            duration: 4000,
          }}
        />
        </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

/**
 * Guards the /p2p/* routes. While the public config is loading we render
 * nothing (no flicker between "blocked" and "allowed"). Once loaded, if
 * `p2pEnabled` is false we send the user to /. The backend also returns
 * 404 from /p2p/* endpoints in that state, so deep-linking won't bypass.
 */
function P2PGate({ children }: { children: ReactNode }) {
  const { config, isLoading } = useBridgeConfig()
  if (isLoading) return null
  if (!config?.p2pEnabled) return <Navigate to="/" replace />
  return <>{children}</>
}

export default App
